import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiArrowLeft, FiPackage, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import GetProductById from "../../../Routes/Marketplace/Merchant/lib/getProductById";
import useEditProduct from "../../../Routes/Marketplace/Merchant/lib/fetchEditProduct";
import useDeleteProduct from "../../../Routes/Marketplace/Merchant/lib/useDeleteProduct";
import useProductCategories from "../../../Routes/Marketplace/Merchant/lib/fetchProductCategory";
import { getProductUrl } from "../../../Routes/Marketplace/libs/getProductUrl";
import PageHeader from "../../components/PageHeader";
import ConfirmModal from "../../components/ConfirmModal";
import CategoryProductFields from "../components/CategoryProductFields";
import Spinner from "../../../Spinner/Spinner";
import { getCategoryFormProfile } from "../utils/categoryFormConfig";
import {
  defaultProductForm,
  formatNgnPrice,
  MAX_PRODUCT_IMAGES,
  parsePriceInput,
  PRODUCT_FIELD_LIMITS,
  productToForm,
  validateProductEditForm,
} from "../utils/productForm";
import {
  getProductApprovalBadgeTone,
  getProductApprovalLabel,
  getProductApprovalStatus,
} from "../../utils/productApprovalDisplay";

const IMAGE_ACCEPT = "image/jpeg,image/png,image/gif,image/webp";
const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;
const PLACEHOLDER = "https://via.placeholder.com/400x300?text=No+Image";

const MerchantProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = GetProductById(slug);
  const product = data?.product;
  const productId = product?._id;

  const { data: categoryData, isLoading: categoriesLoading, isError: categoriesError } = useProductCategories();
  const categories = categoryData?.categories || [];
  const { editProduct, isLoading: saving } = useEditProduct();
  const { deleteProduct, isLoading: deleting } = useDeleteProduct();

  const [form, setForm] = useState(defaultProductForm);
  const [priceDisplay, setPriceDisplay] = useState(formatNgnPrice(0));
  const [priceValue, setPriceValue] = useState(0);
  const [slashDisplay, setSlashDisplay] = useState("");
  const [slashValue, setSlashValue] = useState(0);
  const [newImages, setNewImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [errors, setErrors] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const profile = useMemo(() => getCategoryFormProfile(form.category), [form.category]);

  const newPreviewUrls = useMemo(
    () => newImages.map((file) => URL.createObjectURL(file)),
    [newImages]
  );

  useEffect(
    () => () => {
      newPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    },
    [newPreviewUrls]
  );

  useEffect(() => {
    if (!product?.slug || !slug) return;
    if (OBJECT_ID_PATTERN.test(slug) && product.slug !== slug) {
      navigate(getProductUrl(product), { replace: true });
    }
  }, [product, slug, navigate]);

  useEffect(() => {
    if (!product || initialized) return;
    setForm(productToForm(product));
    setPriceValue(product.price || 0);
    setPriceDisplay(formatNgnPrice(product.price));
    setSlashValue(product.compareAtPrice || 0);
    setSlashDisplay(product.compareAtPrice ? formatNgnPrice(product.compareAtPrice) : "");
    setRemovedImages([]);
    setNewImages([]);
    setInitialized(true);
  }, [product, initialized]);

  const existingImages = product?.images || [];

  const handleCategoryChange = (category) => {
    setForm((prev) => ({
      ...defaultProductForm,
      category,
      name: prev.name,
      description: prev.description,
      quantity: prev.quantity,
      brand: prev.brand,
      attributes: {},
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") {
      handleCategoryChange(value);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAttributeChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      attributes: { ...prev.attributes, [key]: value },
    }));
  };

  const handlePriceChange = (e) => {
    const amount = parsePriceInput(e.target.value);
    setPriceValue(amount);
    setPriceDisplay(formatNgnPrice(amount));
  };

  const handleSlashPriceChange = (e) => {
    const raw = e.target.value;
    if (!raw.trim()) {
      setSlashValue(0);
      setSlashDisplay("");
      return;
    }
    const amount = parsePriceInput(raw);
    setSlashValue(amount);
    setSlashDisplay(formatNgnPrice(amount));
  };

  const handleNewImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setNewImages((prev) => [...prev, ...files].slice(0, MAX_PRODUCT_IMAGES));
    e.target.value = "";
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleRemoveExisting = (url) => {
    setRemovedImages((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productId) return;

    const validationErrors = validateProductEditForm({
      form,
      price: priceValue,
      compareAtPrice: slashValue || null,
      existingImages,
      removedImages,
      newImages,
    });
    if (validationErrors.length) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    try {
      const result = await editProduct.mutateAsync({
        id: productId,
        form,
        price: priceValue,
        compareAtPrice: slashValue,
        newImages,
        removedImages,
      });
      toast.success(result.message || "Product updated");
      if (result?.product) {
        setForm(productToForm(result.product));
        setPriceValue(result.product.price || 0);
        setPriceDisplay(formatNgnPrice(result.product.price));
        setSlashValue(result.product.compareAtPrice || 0);
        setSlashDisplay(
          result.product.compareAtPrice ? formatNgnPrice(result.product.compareAtPrice) : ""
        );
        setRemovedImages([]);
        setNewImages([]);
      } else {
        setInitialized(false);
      }
    } catch (err) {
      setErrors([err.message || "Could not update product."]);
      toast.error(err.message || "Could not update product");
    }
  };

  const handleDelete = async () => {
    if (!productId) return;
    try {
      await deleteProduct.mutateAsync(productId);
      setDeleteOpen(false);
      toast.success("Product deleted");
    } catch (_) {
      toast.error("Could not delete product");
    }
  };

  if (isLoading) {
    return (
      <>
        <Link to="/merchant/products" className="dashboard-back-link">
          <FiArrowLeft aria-hidden="true" /> Back to products
        </Link>
        <Spinner />
      </>
    );
  }

  if (isError || !product) {
    return (
      <>
        <Link to="/merchant/products" className="dashboard-back-link">
          <FiArrowLeft aria-hidden="true" /> Back to products
        </Link>
        <div className="dashboard-card">
          <p style={{ margin: 0, color: "var(--dash-danger)" }}>Could not load this product.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Link to="/merchant/products" className="dashboard-back-link">
        <FiArrowLeft aria-hidden="true" /> Back to products
      </Link>

      <PageHeader
        title={product.name}
        subtitle={`Edit listing · ${product.category}`}
        icon={FiPackage}
        actions={
          <button type="button" className="dashboard-btn dashboard-btn--danger" onClick={() => setDeleteOpen(true)}>
            <FiTrash2 /> Delete
          </button>
        }
      />

      {errors.length > 0 && (
        <div className="dashboard-banner dashboard-banner--error" role="alert">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {getProductApprovalStatus(product) === "rejected" && (
        <div className="dashboard-banner dashboard-banner--error" role="alert">
          <strong>Rejected — not live on marketplace</strong>
          <p style={{ margin: "8px 0 0" }}>
            {product.rejectionReason || "Please review your listing and save changes to resubmit for approval."}
          </p>
        </div>
      )}

      {getProductApprovalStatus(product) === "pending" && (
        <div className="dashboard-banner dashboard-banner--warning" role="status">
          This product is pending admin review and is not visible on the marketplace yet.
          {product.approvalChangeLog?.length > 0 && (
            <span> Your latest edits are waiting for approval before going live again.</span>
          )}
        </div>
      )}

      {getProductApprovalStatus(product) === "approved" && (
        <div className="dashboard-banner dashboard-banner--success" role="status">
          <span className={`dashboard-badge dashboard-badge--${getProductApprovalBadgeTone("approved")}`}>
            {getProductApprovalLabel("approved")}
          </span>
          <span style={{ marginLeft: 8 }}>Live on the marketplace. Saving changes will send it back for review.</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="dashboard-product-form">
        <section className="dashboard-card dashboard-category-picker">
          <h2 className="dashboard-card__title">Category *</h2>
          <p className="dashboard-form-hint">Changing category updates which fields are shown below.</p>

          <select
            name="category"
            value={form.category}
            onChange={handleChange}
            required
            disabled={categoriesLoading || categoriesError}
            className="dashboard-category-picker__select"
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat.categoryName}>
                {cat.categoryName}
              </option>
            ))}
          </select>

          {form.category && (
            <div className="dashboard-category-chip">
              Listing as: <strong>{profile.title}</strong>
            </div>
          )}
        </section>

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Photos *</h2>
          <p className="dashboard-form-hint">
            Up to {MAX_PRODUCT_IMAGES} images. Click an existing image to mark it for removal.
          </p>

          <div className="dashboard-image-upload-grid">
            {existingImages.map((url) => {
              const marked = removedImages.includes(url);
              return (
                <button
                  key={url}
                  type="button"
                  className={`dashboard-image-upload-preview dashboard-image-existing${marked ? " dashboard-image-existing--removed" : ""}`}
                  onClick={() => toggleRemoveExisting(url)}
                  aria-label={marked ? "Undo remove image" : "Mark image for removal"}
                >
                  <img src={url || PLACEHOLDER} alt="" />
                  <span className="dashboard-image-existing__badge">{marked ? "Remove" : "Keep"}</span>
                </button>
              );
            })}

            {newPreviewUrls.map((url, index) => (
              <div key={url} className="dashboard-image-upload-preview">
                <img src={url} alt={`New ${index + 1}`} />
                <button
                  type="button"
                  className="dashboard-image-upload-remove"
                  onClick={() => removeNewImage(index)}
                  aria-label="Remove new image"
                >
                  <FiX />
                </button>
              </div>
            ))}

            {existingImages.length - removedImages.length + newImages.length < MAX_PRODUCT_IMAGES && (
              <label className="dashboard-image-upload-slot">
                <FiPlus aria-hidden="true" />
                <span>Add images</span>
                <input type="file" accept={IMAGE_ACCEPT} multiple onChange={handleNewImages} />
              </label>
            )}
          </div>
        </section>

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Basic info</h2>
          <div className="dashboard-form-grid">
            <label className="dashboard-form-field dashboard-form-field--full">
              <span>
                Product name *
                <span className="dashboard-char-count">
                  {form.name.length}/{PRODUCT_FIELD_LIMITS.name}
                </span>
              </span>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={profile.namePlaceholder}
                maxLength={PRODUCT_FIELD_LIMITS.name}
                required
              />
            </label>

            <label className="dashboard-form-field">
              <span>Selling price (NGN) *</span>
              <input type="text" value={priceDisplay} onChange={handlePriceChange} inputMode="numeric" required />
            </label>

            <label className="dashboard-form-field">
              <span>Slash price (was)</span>
              <input
                type="text"
                value={slashDisplay}
                onChange={handleSlashPriceChange}
                inputMode="numeric"
                placeholder="Optional — original price before discount"
              />
            </label>

            {!profile.showSizes && (
              <label className="dashboard-form-field">
                <span>Stock quantity</span>
                <input
                  type="number"
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  min="0"
                  placeholder="0"
                />
              </label>
            )}
          </div>
        </section>

        {form.category && (
          <CategoryProductFields
            profile={profile}
            form={form}
            onChange={handleChange}
            onAttributeChange={handleAttributeChange}
          />
        )}

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Description *</h2>
          <label className="dashboard-form-field dashboard-form-field--full">
            <span>
              Description *
              <span className="dashboard-char-count">
                {form.description.length}/{PRODUCT_FIELD_LIMITS.description}
              </span>
            </span>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={6}
              placeholder={profile.descriptionPlaceholder}
              maxLength={PRODUCT_FIELD_LIMITS.description}
              required
            />
          </label>
        </section>

        <div className="dashboard-form-actions">
          <Link to="/merchant/products" className="dashboard-btn dashboard-btn--secondary">
            Cancel
          </Link>
          <button type="submit" className="dashboard-btn dashboard-btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      <ConfirmModal
        open={deleteOpen}
        title="Delete product"
        message="This permanently removes the product from your store. This cannot be undone."
        confirmLabel="Delete"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
};

export default MerchantProductDetailPage;
